import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Shield, TrendingUp, AlertTriangle, CheckCircle2, 
  BarChart3, PieChart, Users, Layers, RefreshCw,
  ChevronRight, ArrowUpRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComplianceDashboardWidget = ({ compact = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/uspcf/v2/compliance-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center h-48">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full border-destructive">
        <CardContent className="flex items-center justify-center h-48">
          <div className="text-center">
            <AlertTriangle className="w-8 h-8 text-destructive mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={loadDashboardData}>
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return <CompactDashboard data={data} navigate={navigate} />;
  }

  return <FullDashboard data={data} navigate={navigate} onRefresh={loadDashboardData} />;
};

// Compact version for sidebar or small spaces
const CompactDashboard = ({ data, navigate }) => {
  const avgCoverage = Math.round(
    data.framework_coverage.reduce((sum, f) => sum + f.percentage, 0) / data.framework_coverage.length
  );

  return (
    <Card className="w-full bg-gradient-to-br from-slate-900 to-slate-800 text-white">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-400" />
          Compliance Overview
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-3xl font-bold">{avgCoverage}%</p>
            <p className="text-xs text-slate-400">Avg. Framework Coverage</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">{data.summary.total_controls}</p>
            <p className="text-xs text-slate-400">USPCF Controls</p>
          </div>
        </div>
        
        <div className="space-y-2">
          {data.framework_coverage.slice(0, 3).map(fw => (
            <div key={fw.name} className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: fw.color }}></div>
              <span className="text-xs flex-1">{fw.name}</span>
              <span className="text-xs font-medium">{fw.percentage}%</span>
            </div>
          ))}
        </div>
        
        <Button 
          variant="secondary" 
          size="sm" 
          className="w-full"
          onClick={() => navigate('/grc/uspcf')}
        >
          View Full Dashboard <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
};

// Full dashboard version
const FullDashboard = ({ data, navigate, onRefresh }) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Compliance Dashboard</h2>
          <p className="text-muted-foreground">
            Real-time compliance posture across {data.summary.total_frameworks} frameworks
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => navigate('/grc/controls')}>
            View Controls <ArrowUpRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Controls</p>
                <p className="text-3xl font-bold">{data.summary.total_controls}</p>
              </div>
              <Layers className="w-10 h-10 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Control Domains</p>
                <p className="text-3xl font-bold">{data.summary.total_domains}</p>
              </div>
              <PieChart className="w-10 h-10 text-purple-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Highest Coverage</p>
                <p className="text-2xl font-bold">{data.quick_stats.highest_coverage.name}</p>
                <p className="text-sm text-green-600">{data.quick_stats.highest_coverage.percentage}%</p>
              </div>
              <TrendingUp className="w-10 h-10 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top Owner</p>
                <p className="text-2xl font-bold">{data.quick_stats.top_owner?.owner || 'N/A'}</p>
                <p className="text-sm text-muted-foreground">{data.quick_stats.top_owner?.count || 0} controls</p>
              </div>
              <Users className="w-10 h-10 text-amber-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="frameworks" className="space-y-4">
        <TabsList>
          <TabsTrigger value="frameworks">
            <Shield className="w-4 h-4 mr-2" />
            Framework Coverage
          </TabsTrigger>
          <TabsTrigger value="domains">
            <PieChart className="w-4 h-4 mr-2" />
            By Domain
          </TabsTrigger>
          <TabsTrigger value="owners">
            <Users className="w-4 h-4 mr-2" />
            By Owner
          </TabsTrigger>
        </TabsList>

        {/* Framework Coverage Tab */}
        <TabsContent value="frameworks">
          <Card>
            <CardHeader>
              <CardTitle>Framework Mapping Coverage</CardTitle>
              <CardDescription>
                How many USPCF controls map to each compliance framework
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {data.framework_coverage.map(fw => (
                <div key={fw.name} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded" 
                        style={{ backgroundColor: fw.color }}
                      ></div>
                      <span className="font-medium">{fw.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">
                        {fw.mapped} / {fw.total}
                      </Badge>
                      <span className="font-bold text-lg" style={{ color: fw.color }}>
                        {fw.percentage}%
                      </span>
                    </div>
                  </div>
                  <Progress 
                    value={fw.percentage} 
                    className="h-3"
                    style={{ 
                      '--progress-background': fw.color 
                    }}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Domains Tab */}
        <TabsContent value="domains">
          <Card>
            <CardHeader>
              <CardTitle>Controls by Domain</CardTitle>
              <CardDescription>
                Distribution of controls across USPCF domains
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.controls_by_domain.map((domain, idx) => {
                  const colors = [
                    'bg-blue-500', 'bg-green-500', 'bg-purple-500', 
                    'bg-amber-500', 'bg-red-500', 'bg-cyan-500',
                    'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
                    'bg-orange-500', 'bg-slate-500'
                  ];
                  const color = colors[idx % colors.length];
                  
                  return (
                    <div 
                      key={domain.name}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                      onClick={() => navigate(`/grc/controls?domain=${encodeURIComponent(domain.name)}`)}
                    >
                      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center text-white font-bold`}>
                        {domain.total}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{domain.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {domain.percentage}% of total controls
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Owners Tab */}
        <TabsContent value="owners">
          <Card>
            <CardHeader>
              <CardTitle>Control Ownership Distribution</CardTitle>
              <CardDescription>
                Who is responsible for each control category
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {data.owner_distribution.map((owner, idx) => (
                  <div key={owner.owner} className="flex items-center gap-4">
                    <div className="w-32 text-sm font-medium truncate">{owner.owner}</div>
                    <div className="flex-1">
                      <Progress value={owner.percentage} className="h-6" />
                    </div>
                    <div className="w-20 text-right">
                      <Badge variant="outline">{owner.count} controls</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ComplianceDashboardWidget;
